import React, { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { StyledTextField } from '@modules/ui-components';
import { countUnicodeGraphemes, localeDirection } from '@modules/shared-locales';
import type {
  AdCreativeItem,
  AdCreativeLocalizationEntry,
  AdCreativeLocalizationManifest,
  AdCreativeLocalizationOperation,
  AdCreativeLocalizedCopyCard,
} from '@segmently/schema-models/ad-creative';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { isSegmentlyOwnedCreativeItem } from '../../services/creativeLocaleProjection';

type CarouselLocalizationClient = Pick<
  AdCreativeApiClient,
  'exportItemLocalizationManifest' | 'applyItemLocalization'
>;

export interface CarouselLocalizationEditorProps {
  projectId: string;
  item: AdCreativeItem;
  selectedLocale: string;
  sourceLocale: string;
  client: CarouselLocalizationClient;
  disabled?: boolean;
  onRefresh: () => void | Promise<void>;
}

type CardField = 'headline' | 'body' | 'cta';
type DraftAction = 'set' | 'clear' | 'reuse_source';

interface DraftOperation {
  action: DraftAction;
  value: string;
}

interface ExactTextValue {
  present: boolean;
  value: string;
}

interface CarouselContract {
  cards: AdCreativeLocalizedCopyCard[];
  error: string | null;
}

const CARD_FIELDS: Array<{ key: CardField; label: string }> = [
  { key: 'headline', label: 'Headline' },
  { key: 'body', label: 'Body' },
  { key: 'cta', label: 'CTA' },
];

const ENTRY_KIND_BY_FIELD: Record<CardField, AdCreativeLocalizationEntry['kind']> = {
  headline: 'headline',
  body: 'description',
  cta: 'cta',
};

export function CarouselLocalizationEditor({
  projectId,
  item,
  selectedLocale,
  sourceLocale,
  client,
  disabled = false,
  onRefresh,
}: CarouselLocalizationEditorProps): React.ReactElement {
  const headingId = useId();
  const contract = useMemo(() => inspectCarouselContract(item), [item]);
  const eligibilityError = useMemo(() => carouselEligibilityError({
    projectId,
    item,
    selectedLocale,
    sourceLocale,
    contract,
  }), [contract, item, projectId, selectedLocale, sourceLocale]);
  const editorIdentity = useMemo(() => JSON.stringify({
    projectId,
    itemId: item.id,
    sourceRevision: item.sourceRevision,
    localeRevision: item.localeRevision,
    selectedLocale,
    sourceLocale,
    cardKeys: contract.cards.map((card) => card.cardKey),
  }), [contract.cards, item.id, item.localeRevision, item.sourceRevision, projectId, selectedLocale, sourceLocale]);

  const [manifest, setManifest] = useState<AdCreativeLocalizationManifest | null>(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DraftOperation>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [manifestReload, setManifestReload] = useState(0);

  useEffect(() => {
    let active = true;
    setManifest(null);
    setManifestError(null);
    setDrafts({});
    setSaveStatus(null);
    setSaveError(null);
    if (eligibilityError || contract.cards.length === 0) {
      setManifestLoading(false);
      return () => {
        active = false;
      };
    }

    setManifestLoading(true);
    void client.exportItemLocalizationManifest(projectId, item.id, sourceLocale)
      .then((response) => {
        if (!active) return;
        const nextManifest = response.data?.manifest;
        if (!response.success || !nextManifest) {
          throw new Error(response.error || 'The current localization manifest could not be loaded.');
        }
        assertCurrentCarouselManifest(nextManifest, item, sourceLocale, contract.cards);
        setManifest(nextManifest);
      })
      .catch((error) => {
        if (active) setManifestError(errorMessage(error));
      })
      .finally(() => {
        if (active) setManifestLoading(false);
      });

    return () => {
      active = false;
    };
  }, [client, contract.cards, editorIdentity, eligibilityError, item, manifestReload, projectId, sourceLocale]);

  const entriesByPath = useMemo(() => new Map(
    manifest?.entries.map((entry) => [entry.path, entry]) ?? [],
  ), [manifest]);
  const stagedPaths = useMemo(() => Object.keys(drafts), [drafts]);
  const stagedLimitViolation = useMemo(() => stagedPaths.some((path) => {
    const draft = drafts[path];
    if (!draft || draft.action !== 'set') return false;
    const maxChars = manifestMaxChars(entriesByPath.get(path));
    return maxChars !== null && countUnicodeGraphemes(draft.value) > maxChars;
  }), [drafts, entriesByPath, stagedPaths]);
  const busy = disabled || manifestLoading || saving;

  const stageField = (cardKey: string, field: CardField, value: string): void => {
    const path = cardFieldPath(cardKey, field);
    if (!entriesByPath.has(path)) return;
    setDrafts((current) => ({
      ...current,
      [path]: value.length === 0
        ? { action: 'clear', value: '' }
        : { action: 'set', value },
    }));
    setSaveStatus(null);
    setSaveError(null);
  };

  const stageExplicitBlank = (cardKey: string, field: CardField): void => {
    const path = cardFieldPath(cardKey, field);
    if (!entriesByPath.has(path)) return;
    setDrafts((current) => ({ ...current, [path]: { action: 'clear', value: '' } }));
    setSaveStatus(null);
    setSaveError(null);
  };

  const stageCardSourceReuse = (card: AdCreativeLocalizedCopyCard): void => {
    const next: Record<string, DraftOperation> = {};
    for (const { key: field } of CARD_FIELDS) {
      const path = cardFieldPath(card.cardKey, field);
      if (!entriesByPath.has(path)) continue;
      next[path] = {
        action: 'reuse_source',
        value: exactCardText(card, field, sourceLocale).value,
      };
    }
    setDrafts((current) => ({ ...current, ...next }));
    setSaveStatus(null);
    setSaveError(null);
  };

  const undoField = (cardKey: string, field: CardField): void => {
    const path = cardFieldPath(cardKey, field);
    setDrafts((current) => {
      const next = { ...current };
      delete next[path];
      return next;
    });
    setSaveStatus(null);
    setSaveError(null);
  };

  const saveAll = async (): Promise<void> => {
    if (!manifest || stagedPaths.length === 0 || stagedLimitViolation || eligibilityError) return;
    setSaving(true);
    setSaveError(null);
    setSaveStatus(null);
    try {
      const operations = buildOperations(contract.cards, manifest, drafts);
      const dryRun = await client.applyItemLocalization(
        projectId,
        item.id,
        selectedLocale,
        manifest,
        operations,
        { dryRun: true },
      );
      if (!dryRun.success || !dryRun.data?.result) {
        throw new Error(dryRun.error || 'Carousel localization validation failed. Nothing was applied.');
      }

      const applied = await client.applyItemLocalization(
        projectId,
        item.id,
        selectedLocale,
        manifest,
        operations,
      );
      if (!applied.success || !applied.data?.result) {
        throw new Error(applied.error || 'The atomic carousel localization apply failed.');
      }

      setDrafts({});
      setSaveStatus(`${operations.length} carousel field${operations.length === 1 ? '' : 's'} saved atomically for ${selectedLocale}. Review is still required.`);
      try {
        await onRefresh();
      } catch (refreshError) {
        setSaveError(`Changes were saved, but the refreshed creative could not be loaded: ${errorMessage(refreshError)}`);
      }
    } catch (error) {
      setSaveError(`${errorMessage(error)} No carousel field was applied by this attempt.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ minWidth: 0 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography id={headingId} component="h3" variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Carousel copy localization
          </Typography>
          {contract.cards.length > 0 && <Chip size="small" variant="outlined" label={`${contract.cards.length} cards`} />}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          The current Segmently-copy card order and stable card keys stay fixed. This editor does not combine dynamic candidates, change card media, or mutate anything in Meta.
        </Typography>

        {eligibilityError && <Alert severity="warning">{eligibilityError}</Alert>}
        {!eligibilityError && contract.cards.length === 0 && (
          <Alert severity="info">This Segmently copy has no carousel card copy to localize.</Alert>
        )}
        {manifestLoading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={16} />
            <Typography variant="caption">Loading the current immutable card manifest…</Typography>
          </Stack>
        )}
        {manifestError && (
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => setManifestReload((current) => current + 1)}
                disabled={disabled || manifestLoading}
              >
                Retry
              </Button>
            )}
          >
            {manifestError}
          </Alert>
        )}
        {saveError && (
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                disabled={disabled || saving}
                onClick={() => { void onRefresh(); }}
              >
                Refresh creative
              </Button>
            )}
          >
            {saveError}
          </Alert>
        )}
        {saveStatus && <Alert severity="success">{saveStatus}</Alert>}

        {!eligibilityError && contract.cards.map((card, cardIndex) => {
          const availableFieldCount = CARD_FIELDS.filter(({ key }) => entriesByPath.has(cardFieldPath(card.cardKey, key))).length;
          return (
            <Box
              component="article"
              key={card.cardKey}
              aria-labelledby={`${headingId}-card-${cardIndex}`}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography
                    id={`${headingId}-card-${cardIndex}`}
                    component="h4"
                    variant="body2"
                    sx={{ fontWeight: 700, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                  >
                    {`Card ${cardIndex + 1} · ${card.cardKey}`}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || !manifest || availableFieldCount === 0}
                    aria-label={`Reuse source copy for card ${card.cardKey}`}
                    onClick={() => stageCardSourceReuse(card)}
                  >
                    Reuse source for card
                  </Button>
                </Stack>

                {CARD_FIELDS.map(({ key: field, label }) => {
                  const path = cardFieldPath(card.cardKey, field);
                  const entry = entriesByPath.get(path);
                  const source = exactCardText(card, field, sourceLocale);
                  const target = exactCardText(card, field, selectedLocale);
                  const draft = drafts[path];
                  const value = draft?.value ?? target.value;
                  const maxChars = manifestMaxChars(entry);
                  const length = countUnicodeGraphemes(value);
                  const overLimit = draft?.action === 'set' && maxChars !== null && length > maxChars;
                  const stateLabel = localizedFieldStateLabel(target, draft, selectedLocale);
                  const fieldDisabled = busy || !manifest || !entry;

                  return (
                    <Box
                      key={field}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.8fr) minmax(0, 1.2fr)' },
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        role="group"
                        aria-label={`${label} source for ${card.cardKey}`}
                        lang={sourceLocale}
                        dir={localeDirection(sourceLocale)}
                        sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1, minWidth: 0 }}
                      >
                        <Typography variant="caption" color="text.secondary" component="div">
                          {`${label} · source · ${sourceLocale}`}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {source.present
                            ? source.value.length > 0 ? source.value : 'Explicit blank in source'
                            : 'Not present in source manifest'}
                        </Typography>
                      </Box>

                      <Stack
                        spacing={0.5}
                        lang={selectedLocale}
                        dir={localeDirection(selectedLocale)}
                        sx={{ minWidth: 0 }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            color={draft ? 'info' : 'default'}
                            variant={draft ? 'filled' : 'outlined'}
                            label={stateLabel}
                          />
                          {!entry && <Chip size="small" color="warning" label="Not localizable" />}
                        </Stack>
                        <StyledTextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          label={`${label} · ${selectedLocale} · ${card.cardKey}`}
                          lang={selectedLocale}
                          dir={localeDirection(selectedLocale)}
                          value={value}
                          disabled={fieldDisabled}
                          error={overLimit}
                          helperText={maxChars !== null
                            ? `${length}/${maxChars} visible characters${overLimit ? ' · shorten before saving' : ''}`
                            : !entry ? 'The current immutable source manifest has no entry for this field.' : undefined}
                          onChange={(nextValue) => stageField(card.cardKey, field, String(nextValue))}
                        />
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="text"
                            disabled={fieldDisabled || (target.present && target.value === '' && !draft)}
                            aria-label={`Set explicit blank for ${field} on card ${card.cardKey}`}
                            onClick={() => stageExplicitBlank(card.cardKey, field)}
                          >
                            Set explicit blank
                          </Button>
                          {draft && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={busy}
                              aria-label={`Undo ${field} change on card ${card.cardKey}`}
                              onClick={() => undoField(card.cardKey, field)}
                            >
                              Undo
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}

        {!eligibilityError && contract.cards.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {stagedPaths.length === 0
                ? 'Missing values remain missing until you edit, blank, or explicitly reuse source copy.'
                : `${stagedPaths.length} field${stagedPaths.length === 1 ? '' : 's'} staged. All are validated before one atomic apply.`}
            </Typography>
            <Button
              variant="contained"
              disabled={busy || !manifest || stagedPaths.length === 0 || stagedLimitViolation}
              onClick={() => { void saveAll(); }}
            >
              {saving
                ? 'Saving…'
                : stagedPaths.length > 0
                  ? `Save ${stagedPaths.length} staged change${stagedPaths.length === 1 ? '' : 's'}`
                  : 'Save staged changes'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function inspectCarouselContract(item: AdCreativeItem): CarouselContract {
  const cards = item.localizedCopy?.cardCopy;
  if (cards === undefined) return { cards: [], error: null };
  if (!Array.isArray(cards)) return { cards: [], error: 'Carousel card copy is not a valid ordered list.' };
  const seen = new Set<string>();
  for (const card of cards) {
    const cardKey = typeof card?.cardKey === 'string' ? card.cardKey.trim() : '';
    if (!cardKey) return { cards: [], error: 'Every carousel card must have a non-empty stable card key.' };
    if (cardKey !== card.cardKey) return { cards: [], error: `Carousel card key ${card.cardKey} contains unstable surrounding whitespace.` };
    if (cardKey.includes('.')) return { cards: [], error: `Carousel card key ${cardKey} cannot be represented by a stable manifest path.` };
    if (seen.has(cardKey)) return { cards: [], error: `Carousel card key ${cardKey} is duplicated.` };
    seen.add(cardKey);
    for (const { key: field } of CARD_FIELDS) {
      const localized = card[field];
      if (localized === undefined) continue;
      if (!isRecord(localized.translations)) {
        return { cards: [], error: `Carousel ${cardKey} ${field} translations are invalid.` };
      }
      for (const value of Object.values(localized.translations)) {
        if (typeof value !== 'string') {
          return { cards: [], error: `Carousel ${cardKey} ${field} contains a non-text locale value.` };
        }
      }
    }
  }
  return { cards, error: null };
}

function carouselEligibilityError(input: {
  projectId: string;
  item: AdCreativeItem;
  selectedLocale: string;
  sourceLocale: string;
  contract: CarouselContract;
}): string | null {
  const { projectId, item, selectedLocale, sourceLocale, contract } = input;
  if (!isSegmentlyOwnedCreativeItem(item)) {
    return 'Carousel localization is available only for complete v3 Segmently copies. Provider and legacy records remain read-only.';
  }
  if (item.projectId !== projectId) return 'This Segmently copy belongs to another project.';
  if (sourceLocale !== item.defaultLanguage) return 'The source locale must exactly match this Segmently copy’s default language.';
  if (selectedLocale === sourceLocale) return 'This target-localization editor never changes source copy. Use the source editor for the default language.';
  if (!item.languages.includes(selectedLocale)) return 'Add the exact target locale to this Segmently copy before localizing its carousel.';
  if (item.statusByLocale[selectedLocale] === 'archived') return 'Restore this target locale before localizing its carousel.';
  return contract.error;
}

function assertCurrentCarouselManifest(
  manifest: AdCreativeLocalizationManifest,
  item: AdCreativeItem,
  sourceLocale: string,
  cards: AdCreativeLocalizedCopyCard[],
): void {
  if (manifest.schemaVersion !== 1
    || manifest.creativeItemId !== item.id
    || manifest.sourceRevision !== item.sourceRevision
    || manifest.sourceLocale !== sourceLocale
    || !manifest.manifestHash?.trim()) {
    throw new Error('The localization manifest does not match this exact current Segmently copy.');
  }
  const entryIds = new Set<string>();
  const entriesByPath = new Map<string, AdCreativeLocalizationEntry>();
  for (const entry of manifest.entries) {
    if (!entry.entryId?.trim() || entryIds.has(entry.entryId)) {
      throw new Error('The localization manifest contains duplicate or missing entry identities.');
    }
    entryIds.add(entry.entryId);
    if (entriesByPath.has(entry.path)) {
      throw new Error(`The localization manifest contains duplicate path ${entry.path}.`);
    }
    entriesByPath.set(entry.path, entry);
  }

  const expectedCardPaths = new Set<string>();
  for (const card of cards) {
    for (const { key: field } of CARD_FIELDS) {
      const source = exactCardText(card, field, sourceLocale);
      if (!source.present) continue;
      const path = cardFieldPath(card.cardKey, field);
      expectedCardPaths.add(path);
      const entry = entriesByPath.get(path);
      if (!entry
        || entry.kind !== ENTRY_KIND_BY_FIELD[field]
        || entry.sourceLocale !== sourceLocale
        || entry.sourceValue !== source.value
        || entry.constraints?.cardKey !== card.cardKey) {
        throw new Error(`The localization manifest entry for ${card.cardKey} ${field} is stale or mismatched.`);
      }
    }
  }

  for (const entry of manifest.entries) {
    if (entry.path.startsWith('localizedCopy.cardCopy.') && !expectedCardPaths.has(entry.path)) {
      throw new Error(`The localization manifest contains an unselected carousel path: ${entry.path}.`);
    }
  }
}

function buildOperations(
  cards: AdCreativeLocalizedCopyCard[],
  manifest: AdCreativeLocalizationManifest,
  drafts: Record<string, DraftOperation>,
): AdCreativeLocalizationOperation[] {
  const entriesByPath = new Map(manifest.entries.map((entry) => [entry.path, entry]));
  const operations: AdCreativeLocalizationOperation[] = [];
  for (const card of cards) {
    for (const { key: field } of CARD_FIELDS) {
      const path = cardFieldPath(card.cardKey, field);
      const draft = drafts[path];
      if (!draft) continue;
      const entry = entriesByPath.get(path);
      if (!entry) throw new Error(`The current manifest no longer contains ${card.cardKey} ${field}.`);
      operations.push(draft.action === 'set'
        ? { entryId: entry.entryId, action: 'set', value: draft.value }
        : draft.action === 'clear'
          ? { entryId: entry.entryId, action: 'clear' }
          : { entryId: entry.entryId, action: 'reuse_source' });
    }
  }
  if (operations.length === 0) throw new Error('No carousel fields are staged.');
  return operations;
}

function exactCardText(card: AdCreativeLocalizedCopyCard, field: CardField, locale: string): ExactTextValue {
  const translations = card[field]?.translations;
  if (!translations || !Object.prototype.hasOwnProperty.call(translations, locale)) {
    return { present: false, value: '' };
  }
  return { present: true, value: translations[locale] };
}

function localizedFieldStateLabel(
  target: ExactTextValue,
  draft: DraftOperation | undefined,
  selectedLocale: string,
): string {
  if (draft?.action === 'reuse_source') return 'Source reuse staged';
  if (draft?.action === 'clear') return 'Explicit blank staged';
  if (draft?.action === 'set') return 'Edited value staged';
  if (!target.present) return 'Missing';
  if (target.value === '') return 'Explicit blank';
  return `Exact ${selectedLocale}`;
}

function manifestMaxChars(entry: AdCreativeLocalizationEntry | undefined): number | null {
  const value = entry?.constraints?.maxChars;
  return Number.isSafeInteger(value) && Number(value) > 0 ? Number(value) : null;
}

function cardFieldPath(cardKey: string, field: CardField): string {
  return `localizedCopy.cardCopy.${cardKey}.${field}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
